import React from "react"
import { Button, Spin, Tooltip, Avatar } from "antd"
import {
	MessageOutlined,
	SendOutlined,
	PlusOutlined,
	SmileOutlined,
	TeamOutlined,
	EditOutlined,
	DownOutlined,
	RobotOutlined,
	PictureOutlined,
	PaperClipOutlined,
	InboxOutlined,
} from "@ant-design/icons"
import { Group, GroupMember, Message } from "firebase/groupsService"
import { MessageList } from "./MessageList"
import { MentionPicker } from "./MentionPicker"
import { getAvatarColors } from "../utils"
import strings from "utils/strings"

interface ChatPanelProps {
	selectedGroup: Group | null
	messages: Message[]
	loadingMessages: boolean
	loggedUserId: string
	showScrollBtn: boolean
	floatingDate: string | null
	chatMessagesRef: React.RefObject<HTMLDivElement>
	messagesEndRef: React.RefObject<HTMLDivElement>
	scrollToBottom: (smooth?: boolean) => void
	handleChatScroll: () => void
	typingNames: string[]
	mentionQuery: string | null
	mentionIndex: number
	mentionPickerRef: React.RefObject<HTMLDivElement>
	mentionMembers: GroupMember[]
	insertMention: (member: GroupMember) => void
	inputText: string
	sending: boolean
	uploading: boolean
	imageInputRef: React.RefObject<HTMLInputElement>
	fileInputRef: React.RefObject<HTMLInputElement>
	handleFileClick: () => void
	handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	textareaRef: React.RefObject<HTMLTextAreaElement>
	handleSend: () => void
	handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
	handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
	handleImageClick: () => void
	handleImageFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	onEditGroup: () => void
	onCreateGroup: () => void
	isArchived?: boolean
	botTyping?: boolean
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
	selectedGroup,
	messages,
	loadingMessages,
	loggedUserId,
	showScrollBtn,
	floatingDate,
	chatMessagesRef,
	messagesEndRef,
	scrollToBottom,
	handleChatScroll,
	typingNames,
	mentionQuery,
	mentionIndex,
	mentionPickerRef,
	mentionMembers,
	insertMention,
	inputText,
	sending,
	uploading,
	imageInputRef,
	fileInputRef,
	handleFileClick,
	handleFileChange,
	textareaRef,
	handleSend,
	handleInputChange,
	handleKeyDown,
	handleImageClick,
	handleImageFileChange,
	onEditGroup,
	onCreateGroup,
	isArchived,
	botTyping,
}) => {
	if (!selectedGroup) {
		return (
			<div className="chat-panel">
				<div className="chat-panel__empty">
					<MessageOutlined className="chat-empty-icon" />
					<p>{strings.messaging.selectGroup}</p>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={onCreateGroup}
					>
						{strings.messaging.createGroup}
					</Button>
				</div>
			</div>
		)
	}

	const [c1, c2] = getAvatarColors(selectedGroup.name)

	return (
		<div className="chat-panel">
			<div className="chat-header">
				<Avatar
					size={38}
					style={{
						background: `linear-gradient(135deg, ${c1}, ${c2})`,
						fontWeight: 700,
						flexShrink: 0,
					}}
				>
					{selectedGroup.name[0]?.toUpperCase()}
				</Avatar>
				<div className="chat-header__info">
					<div className="chat-header__name">{selectedGroup.name}</div>
					<div className="chat-header__sub">
						<TeamOutlined style={{ fontSize: 11, marginRight: 3 }} />
						{selectedGroup.memberCount} {strings.messaging.members}
						{selectedGroup.hasBotActive && (
							<span className="chat-header__bot">
								<RobotOutlined style={{ fontSize: 11, marginRight: 3 }} />
								{strings.messaging.botActive}
							</span>
						)}
					</div>
				</div>
				{!isArchived && (
					<Tooltip title={strings.messaging.editGroup}>
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={onEditGroup}
							className="chat-header__edit-btn"
						/>
					</Tooltip>
				)}
			</div>

			<div className="chat-messages-wrap">
				{floatingDate && (
					<div className="chat-floating-date">
						<span>{floatingDate}</span>
					</div>
				)}
				<div
					className="chat-messages"
					ref={chatMessagesRef}
					onScroll={handleChatScroll}
				>
					{loadingMessages ? (
						<div className="chat-messages__loading">
							<Spin />
						</div>
					) : messages.length === 0 ? (
						<div className="chat-messages__empty">
							<SmileOutlined style={{ fontSize: 40, color: "#bdc3c7" }} />
							<p>{strings.messaging.noMessages}</p>
						</div>
					) : (
						<>
							<MessageList
								messages={messages}
								loggedUserId={loggedUserId}
								selectedGroup={selectedGroup}
							/>
							<div ref={messagesEndRef} />
						</>
					)}
				</div>
				{showScrollBtn && (
					<button
						className="scroll-to-bottom-btn"
						onClick={() => scrollToBottom(true)}
					>
						<DownOutlined />
					</button>
				)}
			</div>

			{isArchived && (
				<div className="chat-archived-notice">
					<InboxOutlined style={{ fontSize: 14, marginRight: 6 }} />
					{strings.messaging.archivedNotice}
				</div>
			)}
			<div
				className="chat-input-wrap"
				style={isArchived ? { display: "none" } : undefined}
			>
				{botTyping && (
					<div className="chat-typing-indicator chat-typing-indicator--bot">
						<RobotOutlined style={{ fontSize: 11, marginRight: 4 }} />
						{strings.messaging.botTyping}
					</div>
				)}
				{!botTyping && typingNames.length > 0 && (
					<div className="chat-typing-indicator">
						{typingNames.length === 1
							? strings.messaging.typingOne.replace("{name}", typingNames[0])
							: typingNames.length === 2
								? strings.messaging.typingTwo
										.replace("{name1}", typingNames[0])
										.replace("{name2}", typingNames[1])
								: strings.messaging.typingMany}
					</div>
				)}
				{mentionQuery !== null && (
					<MentionPicker
						members={mentionMembers}
						mentionIndex={mentionIndex}
						pickerRef={mentionPickerRef}
						onSelect={insertMention}
					/>
				)}
				<div className="chat-input-area">
					<input
						ref={imageInputRef}
						type="file"
						accept="image/*"
						style={{ display: "none" }}
						onChange={handleImageFileChange}
					/>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.csv,.xls,.xlsx"
						style={{ display: "none" }}
						onChange={handleFileChange}
					/>
					<Tooltip title={strings.messaging.attachImage}>
						<Button
							type="text"
							icon={<PictureOutlined />}
							onClick={handleImageClick}
							loading={uploading}
							disabled={sending}
							className="chat-attach-btn"
						/>
					</Tooltip>
					<Tooltip title={strings.messaging.attachFile}>
						<Button
							type="text"
							icon={<PaperClipOutlined />}
							onClick={handleFileClick}
							loading={uploading}
							disabled={sending}
							className="chat-attach-btn"
						/>
					</Tooltip>
					<textarea
						ref={textareaRef}
						value={inputText}
						onChange={handleInputChange}
						onKeyDown={handleKeyDown}
						placeholder={strings.messaging.inputPlaceholder}
						className="chat-textarea"
						rows={1}
						disabled={sending || uploading}
						style={{ resize: "none" }}
					/>
					<Button
						type="primary"
						icon={<SendOutlined />}
						onClick={handleSend}
						loading={sending}
						disabled={
							!inputText.trim() ||
							uploading ||
							(botTyping && inputText.toLowerCase().includes("@agentd"))
						}
						className="chat-send-btn"
					/>
				</div>
			</div>
		</div>
	)
}
